import axios from "axios";
import { useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import PanelSkeleton from "../PanelSkeleton";
import { FaRegTrashAlt } from "react-icons/fa";
import { ImSpinner2 } from "react-icons/im";
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import { FiSave, FiRotateCw } from 'react-icons/fi';
import styles from '../../styles/deliverypanel.module.css';

const emptyArea = { area: "", smallVanFees: "", bigVanFees: "" };

const DeliveryPanel = () => {
  const [area, setArea] = useState(emptyArea);
  const [areas, setAreas] = useState([]);
  const [addArea, setAddArea] = useState(false);
  const [editingAreaId, setEditingAreaId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [areaFocus, setAreaFocus] = useState(false);
  const [smallFeesFocus, setSmallFeesFocus] = useState(false);
  const [bigFeesFocus, setBigFeesFocus] = useState(false);
  const formRef = useRef(null);

  const handleActive = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setEditingAreaId(null);
    setArea(emptyArea);
    setAddArea(true);
  };

  const handleInactive = () => {
    if (addArea && formRef.current) {
      formRef.current.style.opacity = '0';
      formRef.current.style.transform = 'translateY(-8px)';
      setTimeout(() => setAddArea(false), 180);
    }
  };

  const fetchZones = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/orders/delivery`, { withCredentials: true });
      setAreas(res.data.data);
    } catch {
      toast.error(<span className="toast">لم نتمكن من تحميل البيانات. حاول مرة اخرى</span>);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchZones(); }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setArea(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setArea(emptyArea);
    setEditingAreaId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleanArea = {
      area: area.area.trim().replace(/\s+/g, ' '),
      smallVanFees: Number(area.smallVanFees),
      bigVanFees: Number(area.bigVanFees),
    };

    try {
      if (editingAreaId) {
        confirmAlert({
          message: `تحديث منطقة التوصيل ؟`,
          buttons: [
            {
              label: 'نعم',
              onClick: async () => {
                setSubmitting(true);
                try {
                  await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/orders/${editingAreaId}/delivery`, cleanArea, { withCredentials: true });
                  toast.success(<span className="toast">تم تحديث منطقة التوصيل بنجاح</span>);
                  resetForm();
                  setAddArea(false);
                  await fetchZones();
                } catch {
                  toast.error(<span className="toast">لم نتمكن من تحديث منطقة التوصيل. حاول مرة اخرى</span>);
                } finally {
                  setSubmitting(false);
                }
              }
            },
            { label: 'لا' }
          ]
        });
      } else {
        confirmAlert({
          message: `إضافة منطقة التوصيل ؟`,
          buttons: [
            {
              label: 'نعم',
              onClick: async () => {
                setSubmitting(true);
                try {
                  await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/orders/delivery`, cleanArea, { withCredentials: true });
                  toast.success(<span className="toast">تم إضافة منطقة التوصيل بنجاح</span>);
                  resetForm();
                  setAddArea(false);
                  await fetchZones();
                } catch {
                  toast.error(<span className="toast">لم نتمكن من إضافة منطقة التوصيل. حاول مرة اخرى</span>);
                } finally {
                  setSubmitting(false);
                }
              }
            },
            { label: 'لا' }
          ]
        });
      }
    } catch {
      toast.error(<span className="toast">حدث خطأ غير متوقع. يرجى المحاولة مرة أخرى</span>);
    }
  };

  const handleEdit = (zone) => {
    setAddArea(true);
    setEditingAreaId(zone.id);
    setArea({ area: zone.area, smallVanFees: zone.smallVanFees, bigVanFees: zone.bigVanFees });
  };

  const handleDelete = (zone) => {
    confirmAlert({
      message: `حذف منطقة ${zone.area} ؟`,
      buttons: [
        {
          label: 'نعم',
          onClick: async () => {
            setSubmitting(true);
            try {
              await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/orders/${zone.id}/delivery`, { withCredentials: true });
              toast.success(<span className="toast">تم حذف المنطقة بنجاح</span>);
              await fetchZones();
            } catch {
              toast.error(<span className="toast">لم نتمكن من حذف المنطقة. حاول مرة اخرى</span>);
            } finally {
              setSubmitting(false);
            }
          }
        },
        { label: 'لا' }
      ]
    });
  };

  if (loading) return <PanelSkeleton count={3} />;

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.tableCard}>

        {/* ── Header ── */}
        <div className={styles.cardHeader}>
          <span className={styles.cardTitle}>مناطق التوصيل</span>
          {addArea ? (
            <button className={styles.cancelBtnInline} onClick={handleInactive} disabled={submitting}>
              إلغاء
            </button>
          ) : (
            <button className={styles.addBtnInline} onClick={handleActive} disabled={submitting}>
              + إضافة منطقة
            </button>
          )}
        </div>

        {/* ── Inline form ── */}
        {addArea && (
          <div ref={formRef} style={{ transition: 'opacity 0.18s ease, transform 0.18s ease' }}>
            <form className={styles.form} onSubmit={handleSubmit}>
              <p className={styles.formTitle}>
                {editingAreaId ? "تحديث منطقة" : "إضافة منطقة"}
              </p>
              <div className={styles.formRow}>
                <div className={styles.fieldWrap}>
                  <label className={`${styles.floatLabel} ${areaFocus || area.area ? styles.floatLabelUp : ''}`}>
                    اسم المنطقة
                  </label>
                  <input
                    type="text"
                    name="area"
                    value={area.area}
                    onChange={handleChange}
                    onFocus={() => setAreaFocus(true)}
                    onBlur={() => setAreaFocus(false)}
                    maxLength={20}
                    required
                    className={styles.input}
                  />
                </div>
                <div className={styles.fieldWrap}>
                  <label className={`${styles.floatLabel} ${smallFeesFocus || area.smallVanFees ? styles.floatLabelUp : ''}`}>
                    سعر السيارة الصغيرة
                  </label>
                  <input
                    type="number"
                    step={0.01}
                    min={1}
                    max={100000}
                    name="smallVanFees"
                    value={area.smallVanFees}
                    onChange={handleChange}
                    onFocus={() => setSmallFeesFocus(true)}
                    onBlur={() => setSmallFeesFocus(false)}
                    required
                    className={styles.input}
                  />
                </div>
                <div className={styles.fieldWrap}>
                  <label className={`${styles.floatLabel} ${bigFeesFocus || area.bigVanFees ? styles.floatLabelUp : ''}`}>
                    سعر السيارة الكبيرة
                  </label>
                  <input
                    type="number"
                    step={0.01}
                    min={1}
                    max={100000}
                    name="bigVanFees"
                    value={area.bigVanFees}
                    onChange={handleChange}
                    onFocus={() => setBigFeesFocus(true)}
                    onBlur={() => setBigFeesFocus(false)}
                    required
                    className={styles.input}
                  />
                </div>
                <button type="submit" disabled={submitting} className={styles.submitBtn}>
                  {submitting
                    ? <ImSpinner2 className={styles.spinner} />
                    : editingAreaId
                      ? <FiRotateCw className={styles.btnIcon} />
                      : <FiSave className={styles.btnIcon} />
                  }
                  {editingAreaId ? "تحديث" : "حفظ"}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* ── Zone list ── */}
        {areas.length === 0 ? (
          <p className={styles.emptyMsg}>لا توجد مناطق توصيل مضافة بعد</p>
        ) : (
          <div className={styles.zoneList}>
            {areas.map((zone, i) => (
              <div
                key={zone.id}
                className={`${styles.zoneRow} ${editingAreaId === zone.id ? styles.zoneRowActive : ''}`}
                style={{ animationDelay: `${i * 0.04}s` }}
              >
                <span className={styles.zoneName}>{zone.area}</span>
                <div className={styles.feesGroup}>
                  <span className={`${styles.feesBadge} ${styles.feesBadgeSmall}`}>
                    <span className={styles.vanLabel}>صغيرة</span>
                    ₪ {Number(zone.smallVanFees).toFixed(2)}
                  </span>
                  <span className={`${styles.feesBadge} ${styles.feesBadgeBig}`}>
                    <span className={styles.vanLabel}>كبيرة</span>
                    ₪ {Number(zone.bigVanFees).toFixed(2)}
                  </span>
                </div>
                <div className={styles.zoneActions}>
                  <button
                    className={styles.editBtn}
                    onClick={() => { handleEdit(zone); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    disabled={submitting}
                  >
                    تعديل
                  </button>
                  <button
                    className={styles.deleteBtn}
                    onClick={() => !submitting && handleDelete(zone)}
                    disabled={submitting}
                  >
                    <FaRegTrashAlt />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
};

export default DeliveryPanel;
